import CloseIcon from '@mui/icons-material/Close';
import { AppBar, Box, Button, Chip, Dialog, DialogActions, DialogContent, IconButton, Stack, TextField, Toolbar, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import defaultGoogleImage from '../../src/assets/googleSmall.png';
import { DEFAULT_EMAIL_MESSAGES } from '../utility/Constant';
import CustomerCar from '../utility/Svg/CustomerCar';
import ReviewRequestAppSvg from '../utility/Svg/ReviewRequestAppSvg';
import ReviewRequestEmailSvg from '../utility/Svg/ReviewRequestEmailSvg';
import ReviewRequestTextSvg from '../utility/Svg/ReviewRequestTextSvg';
import UsersSvg from '../utility/Svg/Users';

const InfoBanner = styled(Box)({
    borderRadius: '4px!important',
    background: 'rgba(0, 0, 0, 0.10)',
    display: 'flex',
    padding: '12px 16px',
    justifyContent: 'flex-start!important',
    alignItems: 'center',
    gap: '8px',
});

const ChipWrapper = styled('div')({
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    width: '100%',
    '& > *': {
        flex: 1,
        minWidth: 0,
    },
});

const StyledChip = styled(Chip)(({ $selected }) => ({
    width: '100%',
    height: '40px',
    fontFamily: 'Roboto',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
    letterSpacing: '0.15px',
    textTransform: 'capitalize',
    paddingLeft: 12,
    paddingRight: 12,
    gap: '0px',

    ...($selected
        ? {
              borderRadius: '50px',
              border: '1px solid #0066E1',
              backgroundColor: '#007AFF',
              boxShadow: '0 2px 2px 0 rgba(0, 122, 255, 0.25)',
              color: '#FFF',
              '&:hover': {
                  backgroundColor: '#007AFF',
                  borderColor: '#0066E1',
                  boxShadow: '0 2px 2px 0 rgba(0, 122, 255, 0.25)',
              },
          }
        : {
              borderRadius: '100px',
              border: '1px solid #DCDCDC',
              backgroundColor: '#FFF',
              boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.06)',
              color: '#000',
              '&:hover': {
                  backgroundColor: '#FFF',
                  borderColor: '#DCDCDC',
                  boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.06)',
              },
          }),
}));

const ReviewRequestModal = ({
    open = true,
    onClose,
    shopConfigData,
    customerName = '',
    carInfo = '',
    shopName = '',
    googleImage = true,
    showInstructionText = '',
    modalTitle = '',
    googleReviewMessage = '',
    sendRequest,
    onClickSkip,
}) => {
    const getDefaultChannels = () => {
        const channels = [];
        if (shopConfigData?.config?.shouldSendReviewSMSToCustomer) channels.push('text');
        if (shopConfigData?.config?.shouldSendReviewEmailToCustomer) channels.push('email');
        if (shopConfigData?.config?.shouldSendReviewNotificationToCustomer) channels.push('app');
        return channels;
    };

    const [selectedChannels, setSelectedChannels] = useState([]);
    console.log('🚀 ~ ReviewRequestModal ~ selectedChannels:', selectedChannels);
    const [customEmailMessage, setCustomEmailMessage] = useState('');

    useEffect(() => {
        if (open) {
            setSelectedChannels(getDefaultChannels() || []);
        }
    }, [open, shopConfigData]);

    const getDynamicPlaceholder = (shopData) => {
        const customMessage = shopData?.shopThankYouEmailCustomMessage || DEFAULT_EMAIL_MESSAGES.OPENING(shopName);

        const closingMessage = shopData?.shopThankYouEmailClosingMessage || DEFAULT_EMAIL_MESSAGES.CLOSING;

        return `${customMessage}${customMessage && closingMessage ? '\n\n' : ''}${closingMessage}`.trim();
    };

    const handleCancel = () => {
        setSelectedChannels([]);
        setCustomEmailMessage('');
        onClose?.();
    };

    const toggleChannel = (channel) => {
        setSelectedChannels((prev) => (prev.includes(channel) ? prev.filter((c) => c !== channel) : [...prev, channel]));
    };

    const renderGoogleIcon = () => {
        if (googleImage === true) return <img src={defaultGoogleImage} alt="google" style={{ marginRight: 0 }} />;
        if (typeof googleImage === 'string') return <img src={googleImage} alt="google" style={{ marginRight: 0 }} />;
        return null;
    };

    const isEmailSelected = selectedChannels.includes('email');

    const handleSendRequest = () => {
        const shouldSendReviewEmailToCustomer = selectedChannels.includes('email');
        const shouldSendReviewSMSToCustomer = selectedChannels.includes('text');
        const shouldSendReviewNotificationToCustomer = selectedChannels.includes('app');
        const params = new URLSearchParams({
            shouldSendReviewEmailToCustomer: shouldSendReviewEmailToCustomer,
            shouldSendReviewSMSToCustomer: shouldSendReviewSMSToCustomer,
            shouldSendReviewNotificationToCustomer: shouldSendReviewNotificationToCustomer,
        });
        const queryString = params.toString();
        // Only pass customMessage in body when user typed something (truthy); do not send when falsy
        const customMessage = isEmailSelected ? customEmailMessage.trim() || undefined : undefined;
        sendRequest(queryString, customMessage);
        setSelectedChannels([]);
        setCustomEmailMessage('');
    };

    const handleSkip = () => {
        const queryString = `shouldSendReviewEmailToCustomer=false&shouldSendReviewSMSToCustomer=false&shouldSendReviewNotificationToCustomer=false`;
        sendRequest(queryString);
        setSelectedChannels([]);
        setCustomEmailMessage('');
        onClickSkip?.();
    };

    const chipIconColor = (selected) => (selected ? '#FFF' : '#000');

    const channels = [
        { id: 'text', label: 'Text', Icon: ReviewRequestTextSvg, size: 18 },
        { id: 'email', label: 'Email', Icon: ReviewRequestEmailSvg, size: 24 },
        { id: 'app', label: 'App', Icon: ReviewRequestAppSvg, size: 24 },
    ];

    return (
        <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth className="commonDialog__500">
            <AppBar position="relative" color="secondary" elevation={0}>
                <Toolbar sx={{ padding: '15px 16px !important' }}>
                    <Typography variant="h6" sx={{ flexGrow: 1, color: '#fff', display: 'flex', alignItems: 'center' }}>
                        {renderGoogleIcon()} {modalTitle}
                    </Typography>
                    <IconButton onClick={handleCancel} size="small">
                        <CloseIcon sx={{ color: '#fff' }} />
                    </IconButton>
                </Toolbar>
            </AppBar>

            <DialogContent sx={{ px: 3, py: 3 }}>
                <Typography variant="body1" sx={{ display: 'flex', fontWeight: 500, alignItems: 'center', gap: 1, fontSize: '18px', marginBottom: '4px' }}>
                    <UsersSvg /> {customerName}
                </Typography>

                <Typography variant="body1" sx={{ display: 'flex', fontWeight: 500, alignItems: 'center', gap: 1, fontSize: '18px' }}>
                    <CustomerCar /> {carInfo}
                </Typography>

                {showInstructionText && <Typography sx={{ fontSize: '16px', fontWeight: 400, padding: '24px 0 12px 0' }}>{showInstructionText}</Typography>}

                <ChipWrapper>
                    {channels.map(({ id, label, Icon, size }) => {
                        const selected = selectedChannels.includes(id);
                        const color = chipIconColor(selected);
                        return (
                            <StyledChip
                                key={id}
                                $selected={selected}
                                onClick={() => toggleChannel(id)}
                                icon={<Icon color={color} size={size} />}
                                label={label}
                                aria-label={`${id} request`}
                                clickable
                            />
                        );
                    })}
                </ChipWrapper>

                {googleReviewMessage && (
                    <Typography
                        sx={{
                            textAlign: 'center',
                            display: 'block',
                            mt: '12px',
                            fontSize: '14px',
                            fontWeight: '400',
                            lineHeight: '150%',
                            letterSpacing: '0.15px',
                            color: 'rgba(0,0,0,0.60)',
                        }}
                    >
                        {googleReviewMessage}
                    </Typography>
                )}

                {isEmailSelected && (
                    <Stack gap="8px" sx={{ mt: 3 }}>
                        <Typography fontSize={16} fontWeight={500} lineHeight="150%">
                            Customize email message
                        </Typography>
                        <TextField
                            multiline
                            value={customEmailMessage}
                            onChange={(e) => setCustomEmailMessage(e.target.value)}
                            placeholder={getDynamicPlaceholder(shopConfigData)}
                            fullWidth
                            variant="outlined"
                            size="small"
                            sx={{
                                '& .MuiInputBase-input::placeholder': {
                                    fontWeight: 400,
                                    lineHeight: '130%',
                                    letterSpacing: '0.15px',
                                    color: '#9E9E9E',
                                    opacity: 1,
                                },
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    padding: '12px!important',
                                    paddingBottom: customEmailMessage ? '12px!important' : '0!important',
                                    boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.06)',
                                    '& fieldset': {
                                        border: '1px solid rgba(0, 0, 0, 0.10)',
                                    },
                                },
                            }}
                        />
                    </Stack>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    px: 2,
                    pb: 2,
                    justifyContent: 'space-between',
                    paddingTop: '16px !important',
                    borderTop: '1px solid #e0e0e0',
                }}
            >
                <Button variant="outlined" color="primary" size="large" onClick={handleSkip}>
                    Skip SENDING
                </Button>
                <Button variant="contained" color="primary" size="large" type="submit" onClick={handleSendRequest}>
                    Send Request
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ReviewRequestModal;
